<div class="space-y-4">
  <app-breadcrumbs [items]="breadcrumbItems" />

  <section class="grid items-start gap-4 xl:grid-cols-[minmax(0,1.8fr)_minmax(320px,1fr)]">
    <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      @if (loading()) {
        <div class="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          Loading services...
        </div>
      } @else if (errorMessage()) {
        <div class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {{ errorMessage() }}
        </div>
      } @else {
        <div class="space-y-6">
          <div class="space-y-3">
            @if (!activeServices().length) {
              <div class="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                No services have been created.
              </div>
            }

            @for (service of activeServices(); track service.id) {
              <article class="rounded-xl border border-slate-200 p-4">
                <div class="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
                  <div>
                    <div class="flex flex-wrap items-center gap-2">
                      <h3 class="text-base font-semibold text-slate-900">{{ service.name }}</h3>
                    </div>
                    <p class="mt-1 text-sm text-slate-600">
                      Service Type:
                      {{ getServiceTypeLabel(service) }}
                    </p>
                    <p class="mt-1 text-sm text-slate-600">Unit Type: {{ service.service_unit_label }}</p>
                    <p class="mt-1 text-sm text-slate-500">{{ service.description || 'No description provided.' }}</p>
                  </div>

                  <div class="flex flex-wrap gap-2 md:justify-end">
                    <button
                      type="button"
                      class="rounded-lg border border-rose-300 bg-rose-50 px-3 py-1.5 text-sm text-rose-800 hover:bg-rose-100"
                      (click)="requestDisableService(service)"
                      [disabled]="saving()"
                    >
                      Disable
                    </button>
                  </div>
                </div>
              </article>
            }
          </div>
        </div>
      }
    </article>

    <aside class="self-start rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div class="mb-4 border-b border-slate-100 pb-4">
        <h2 class="text-lg font-semibold text-slate-900">Create Service</h2>
        <p class="mt-1 text-sm text-slate-500">
          Service names are unique with controlled service types and units.
        </p>
      </div>

      <form class="space-y-4" [formGroup]="serviceForm" (ngSubmit)="submit()">
        <div>
          <label for="service-name" class="mb-1 block text-sm font-medium text-slate-700">Name</label>
          <input
            id="service-name"
            type="text"
            formControlName="name"
            class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            placeholder="Enter service name"
          />
        </div>

        <div>
          <label for="service-type" class="mb-1 block text-sm font-medium text-slate-700">Service Type</label>
          <select
            id="service-type"
            formControlName="serviceType"
            class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
          >
            @for (option of serviceTypeOptions; track option.value) {
              <option [value]="option.value">{{ option.label }}</option>
            }
          </select>
        </div>

        @if (serviceForm.controls.serviceType.value === 'other') {
          <div>
            <label for="service-type-other" class="mb-1 block text-sm font-medium text-slate-700">Other Service Type</label>
            <input
              id="service-type-other"
              type="text"
              formControlName="serviceTypeOther"
              class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              placeholder="Enter custom service type"
            />
          </div>
        }

        <div>
          <label for="service-unit-type" class="mb-1 block text-sm font-medium text-slate-700">Unit Type</label>
          <select
            id="service-unit-type"
            formControlName="unitType"
            class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
          >
            @for (option of unitTypeOptions; track option.value) {
              <option [value]="option.value">{{ option.label }}</option>
            }
          </select>
        </div>

        <div>
          <label for="service-description" class="mb-1 block text-sm font-medium text-slate-700">
            Description
          </label>
          <textarea
            id="service-description"
            rows="4"
            formControlName="description"
            class="w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            placeholder="Optional service description"
          ></textarea>
        </div>

        <div class="flex gap-2">
          <button
            type="submit"
            class="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
            [disabled]="saving()"
          >
            Create Service
          </button>

          <button
            type="button"
            class="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            (click)="startCreate()"
          >
            Reset
          </button>
        </div>
      </form>
    </aside>
  </section>

  @if (isDisableDialogOpen()) {
    <div class="fixed inset-0 z-40 bg-slate-900/50" (click)="cancelDisableService()"></div>

    <div class="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="disable-service-title">
      <article class="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl sm:p-6">
        <h3 id="disable-service-title" class="text-lg font-semibold text-slate-900">Disable Service?</h3>

        <p class="mt-2 text-sm text-slate-600">
          <span class="font-semibold text-slate-900">{{ servicePendingDisable()?.name }}</span>
          will be inactive and unavailable for new case work. Existing cases using this service will not be affected.
        </p>

        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            (click)="cancelDisableService()"
            [disabled]="saving()"
          >
            Cancel
          </button>

          <button
            type="button"
            class="rounded-xl bg-rose-700 px-4 py-2 text-sm font-medium text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-70"
            (click)="confirmDisableService()"
            [disabled]="saving()"
          >
            Confirm Disable
          </button>
        </div>
      </article>
    </div>
  }
</div>
